import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormAutocomplete } from 'src/components/Forms';
import { ConversationAssigneeGroup, useConversationAssigneeGroups } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

interface GroupOption extends Option {
  data: ConversationAssigneeGroup;
}

interface FormValues {
  groupOptions: GroupOption[];
}

export const UpdateUserAssigneeGroupsModal = () => {
  const theme = useTheme();
  const { closeModal } = useModalMethods(Modals.UpdateAssigneeGroups);
  const { isOpened, modalProps } = useModalState(Modals.UpdateAssigneeGroups);

  const { conversationAssigneeGroups } = useConversationAssigneeGroups(
    modalProps?.user.partnerId,
  );

  const groupOptions = useMemo(() => {
    return Object.values(conversationAssigneeGroups ?? {}).map<GroupOption>((group) => ({
      id: group.id,
      label: group.name,
      partnerId: group.partnerId,
      value: group.conversationAssigneeGroupId,
      data: group,
    }));
  }, [conversationAssigneeGroups]);

  const selectedOptions = useMemo(() => {
    const selectedGroupIds = (modalProps?.user.conversationAssigneeGroups ?? []).map(
      (g) => g.id,
    );
    return groupOptions.filter((g) => selectedGroupIds.includes(g.id));
  }, [modalProps, groupOptions]);

  const methods = useForm<FormValues>({
    mode: 'all',
    values: {
      groupOptions: selectedOptions,
    },
  });

  const { formState, reset, handleSubmit } = methods;

  const closeModalAndResetForm = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const onFormSubmit = useCallback(
    (formValues: FormValues) => {
      if (!modalProps?.user.id) {
        return;
      }
      modalProps?.onSubmit({
        userId: modalProps.user.id,
        userConversationAssigneeGroups: formValues.groupOptions.map((group) => group.data),
      });
      closeModalAndResetForm();
    },
    [modalProps, closeModalAndResetForm],
  );

  return (
    <Dialog open={isOpened} onClose={closeModalAndResetForm} fullWidth>
      <DialogTitle>
        <Typography>Update Assignee Groups</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack flexDirection="column">
          <FormProvider {...methods}>
            <Stack sx={{ width: '100%', padding: '1rem' }}>
              <form
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onSubmit={handleSubmit(onFormSubmit)}
              >
                <FormAutocomplete
                  type="autocomplete"
                  multiple
                  name="groupOptions"
                  label="User Assignee Groups"
                  options={groupOptions}
                  textFieldProps={{
                    size: 'small',
                    variant: 'outlined',
                  }}
                />

                <Button
                  type="submit"
                  disabled={!formState.isValid}
                  sx={{ alignSelf: 'flex-end', marginTop: theme.spacing(2) }}
                >
                  Submit
                </Button>
              </form>
            </Stack>
          </FormProvider>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
